import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';
import { AccountService } from 'app/core';
import { RelatedToType } from 'app/shared/enums';
import UrlUtils from 'app/shared/Utils/url.utils';
import { SavedView } from 'app/core/models/account.types';
import { SavedViewsSidebarService } from 'app/shared/services/saved-views-sidebar.service';
import { PermissionUtils } from '@core/utils/permission.utils';
import { FeatureFlag } from '@core/enums';
import { SavedViewsState } from '@state/saved-view/state';
import { Store } from '@ngxs/store';
import { filter } from 'rxjs';

@Component({
  selector: 'properties-list',
  templateUrl: './list.component.html',
  styles: [
    `
      :host {
        height: 100%;

        .active {
          @apply bg-black-100;
        }

        .property-route {
          /*
           same style from ts-tree-menu
          */
          cursor: pointer;
          padding: 4px 8px;
          transition: all 0.2s;
          border-radius: 4px;
          @apply flex flex-row items-center gap-1.5;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertiesListComponent extends OnDestroyMixin implements OnInit {
  public savedViews: SavedView[];
  public savedViewsSidebarConfig: TsPageSidenavConfig;
  public relatedToType = RelatedToType;
  protected readonly FeatureFlag = FeatureFlag;

  constructor(
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly accountService: AccountService,
    private readonly store: Store,
    private readonly savedViewsSidebarService: SavedViewsSidebarService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.store
      .select(SavedViewsState.getViews)
      .pipe(
        filter((savedViews) => !!savedViews),
        untilComponentDestroyed(this),
      )
      .subscribe((savedViews: SavedView[]) => {
        this.savedViews = savedViews
          .filter((savedView: SavedView) => savedView.related_to_type === this.relatedToType.Property)
          .filter(PermissionUtils.canView);
        this.cdRef.markForCheck();
        this.checkRedirectToFirstObjectView(this.activatedRoute);
      });

    this.router.events.pipe(untilComponentDestroyed(this)).subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        this.checkRedirectToFirstObjectView(this.activatedRoute);
      }
    });

    this.savedViewsSidebarConfig = this.savedViewsSidebarService.getSidenavConfig(this.relatedToType.Property);
  }

  private checkRedirectToFirstObjectView(route: ActivatedRoute): void {
    if (!route.snapshot.firstChild && this.savedViews.length > 0) {
      let savedViewId = UrlUtils.getFromLocalStorage('propertiesSavedViewId');
      const isSavedViewAvailable = this.savedViews.find((savedView) => savedView.id === savedViewId);

      if (savedViewId && isSavedViewAvailable) {
        this.router.navigate(['./', savedViewId, 'table'], { relativeTo: route, queryParamsHandling: 'merge' });
      } else {
        const defaultSavedView = this.savedViews.find((savedView) =>
          SavedViewsSidebarService.isDefaultView(savedView.name),
        );
        const redirectSavedView = defaultSavedView || this.savedViews[0];

        this.router.navigate(['./', redirectSavedView?.id, 'table'], {
          relativeTo: route,
          queryParamsHandling: 'merge',
        });
      }
    }
  }
}
